import React, { FC, useState } from 'react';

import { BlueprintExceptionResource } from '@ariksa/compliance-policies/api';
import { Box, useDisclosure, Text, Center } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getIcon,
  renderSeverityBar,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { TableColumnProps } from 'components/DataDisplay/NewTable/types';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { ExceptionsByEnvironment } from 'containers/PolicyHub/Blueprints/Components/Blueprint/Exceptions/ExceptionsByEnvironments';
import {
  renderClouds,
  renderExpiresIn,
} from 'containers/PolicyHub/Blueprints/Components/Blueprint/Exceptions/utils';
import { selectBlueprints } from 'containers/PolicyHub/Blueprints/selectors';
import { actions } from 'containers/PolicyHub/Blueprints/slice';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';

export const ExceptionsTable: FC = () => {
  const dispatch = useDispatch();
  const { exceptions } = useSelector(selectBlueprints);
  const { accountMapping } = useCloudAccountId();
  const byEnvironments = useDisclosure();
  const [
    currentRecord,
    setCurrentRecord,
  ] = useState<BlueprintExceptionResource>({} as BlueprintExceptionResource);
  const { cloudNativeMapping } = useResourceType();

  const columns: TableColumnProps[] = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Description</Box>,
      accessor: 'config_id',
      render: ({ row }) => (
        <WithResourceIcon
          resourceType={row?.native_resource}
          tooltip={
            <Box>{row?.compliance_description || row?.alert_description}</Box>
          }
        >
          <Text whiteSpace="wrap" noOfLines={2}>
            {row?.compliance_description || row?.alert_description}
          </Text>
        </WithResourceIcon>
      ),
      //styles: { width: '300px', cell: { maxWidth: '300px' } },
    },
    {
      header: 'Cloud',
      render: ({ row }) => {
        const resource = cloudNativeMapping?.[row?.native_resource];
        return (
          <Center>
            <Center boxSize={5}>{getIcon(resource?.provider!)}</Center>
          </Center>
        );
      },
      styles: { width: '180px', cell: { maxWidth: '180px' } },
    },
    {
      header: 'Severity',
      accessor: 'severity',
      render: ({ value }) => renderSeverityBar({ value, isInline: false }),
      align: 'left',
      styles: { width: '180px', cell: { maxWidth: '180px' } },
    },
    {
      header: 'Environments',
      accessor: 'environment_ids',
      render: ({ value }) => value?.length ?? 0,
      styles: { width: '220px', cell: { maxWidth: '220px' } },
    },
    {
      header: 'Accounts',
      render: ({ row }) =>
        renderClouds(row?.account_ids, 'accounts', accountMapping),
      styles: { width: '220px', cell: { maxWidth: '220px' } },
    },
    {
      header: 'Expires in',
      align: 'left',
      render: renderExpiresIn,
      styles: { width: '180px', cell: { maxWidth: '180px' } },
    },
  ];

  return (
    <Box h={'full'} w={'full'}>
      <Table
        columns={columns}
        data={exceptions.data}
        isLoading={exceptions.isLoading}
        onRowClick={row => {
          setCurrentRecord(row);
          byEnvironments.onOpen();
        }}
        pagination={{
          pageInfo: exceptions.page.info,
          totalCount: exceptions.page.totalCount,
          onChange: pageInfo =>
            dispatch(actions.updateExceptionsPagination(pageInfo)),
        }}
        styles={{ header: { zIndex: 800 } }}
      />
      {byEnvironments.isOpen && (
        <ExceptionsByEnvironment
          row={currentRecord}
          isOpen={byEnvironments.isOpen}
          onClose={byEnvironments.onClose}
        />
      )}
    </Box>
  );
};
